import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Badge, Button, Card, IconBadge, type IconBadgeProps } from '@monksflow/monks-ui';
import { ArrowRightIcon, type LucideIcon } from 'lucide-react';
import clsx from 'clsx';

/** IconBadge's hue variants only; its status and structural ones mean something else here. */
export type KnowledgeHubCardTint = Extract<
  NonNullable<IconBadgeProps['variant']>,
  'red' | 'orange' | 'yellow' | 'lime' | 'blue' | 'purple' | 'pink' | 'teal'
>;

export interface KnowledgeHubCardProps {
  /** Lucide icon rendered in the tinted tile at the top-left of the card. */
  icon: LucideIcon;
  /** Card title rendered as an h3. */
  title: string;
  /** Card body description (multi-line). */
  description: string;
  /**
   * Count rendered in the badge under the description. Zero renders too, muted:
   * on a hub tile it is the most actionable number there is — no connected
   * source, no enforced standard, an empty library — and hiding it made a
   * workspace with unfinished setup look identical to a healthy one. Omit the
   * prop entirely for a card that has nothing worth counting, and for one whose
   * count could not be fetched: zero must mean the section is empty, never that
   * the request failed.
   */
  count?: number;
  /** Noun following the count, e.g. "bases", "audiences". */
  countLabel?: string;
  /** Route to navigate to when the card is activated (click / Enter). */
  to: string;
  /**
   * Tile hue. Omit for the neutral `secondary` tile, not `muted`: `--muted` and
   * `--card` are the same stone-800 in dark mode, so a muted tile disappears
   * into the card.
   */
  tint?: KnowledgeHubCardTint;
}

/**
 * A top-level Knowledge destination, rendered as one tile in the hub grid.
 *
 * The card is its own `bg-card` surface: the grid behind it is the page canvas,
 * not a container, so nothing else supplies the fill. Its border stays
 * transparent until hover, which is the only affordance distinguishing a tile
 * from a static panel.
 *
 * Rendered as a `<Link>` so it inherits real anchor semantics — tab focus,
 * Enter activation, and a URL a screen reader can announce.
 *
 * The hover cue is a cue, not a target: the whole card navigates, so making the
 * label and arrow the only hit region would shrink a 430x290 target to a 100px
 * one and strand every touch device, which has no hover to reveal it with.
 *
 * That is why the Button is `asChild` over a `<span>`. Interactive content
 * cannot nest inside interactive content, so a real `<button>` here would be
 * invalid markup, a second tab stop, and a click target that swallows the
 * anchor underneath it. `asChild` takes the Tessra treatment and leaves the
 * element inert.
 *
 * It is `aria-hidden` for the same reason it is hidden until hover — it repeats
 * what the anchor already announces. A screen reader reaching this card is told
 * the title, the description and the destination; "View" on top of
 * that is a third way of saying the same thing. Hiding a real `<button>` from
 * the tree while leaving it focusable would be its own defect; the span cannot
 * be focused, so there is nothing stranded behind the `aria-hidden`.
 *
 * The reveal rides on the wrapper rather than on the Button, so the Button
 * keeps its own transition and `duration-fast` untouched — retiming a design
 * system component on one instance is a change that belongs upstream.
 *
 * It transitions `translate`, not `transform`. Tailwind v4 emits `-translate-x-1`
 * as the standalone `translate` property, so a list naming `transform` leaves the
 * movement untransitioned: it snapped the full 4px in one frame while the cue was
 * still opaque, which read as the button flicking sideways on mouse-out. This is
 * also why the library's own `transition-transform` expands to four properties.
 *
 * The title sits a fixed distance below the icon tile rather than being pushed
 * down by what follows it. `justify-between` here anchored the text block to the
 * card foot, so the title's height depended on the description's line count and
 * on whether a count badge rendered — a card without a badge dropped its title
 * roughly 50px below its neighbour's, on a grid whose whole job is to be read
 * across. The leftover height goes to the text block instead, and `mt-auto` on
 * the badge keeps the second axis `justify-between` used to give for free.
 *
 * It deliberately ships NO focus styles of its own. The grid above it scrolls,
 * and an indicator painted outside the border box gets sliced off against that
 * container — which is every card in the first row and the first column. The
 * library's `:focus-visible` fallback is pulled inset app-wide in `global.css`
 * for exactly this, so leaving it alone is what keeps the ring whole.
 */
export const KnowledgeHubCard = ({
  icon: Icon,
  title,
  description,
  count,
  countLabel,
  to,
  tint,
}: KnowledgeHubCardProps) => {
  const { t } = useTranslation('knowledge');
  const showCount = count !== undefined;

  return (
    <Card
      data-testid="knowledge-hub-card"
      className="group h-full gap-0 rounded-lg border border-transparent bg-card p-0 text-card-foreground shadow-none transition-colors duration-base ease-out hover:border-border"
    >
      <Link
        to={to}
        data-testid="knowledge-hub-card-body"
        className="flex h-full flex-col items-start gap-2xl rounded-lg p-section no-underline"
      >
        <div className="flex w-full items-center justify-between gap-md">
          <IconBadge data-testid="knowledge-hub-card-icon" variant={tint ?? 'secondary'} size="lg">
            <Icon data-testid="knowledge-hub-card-icon-svg" className="size-5" />
          </IconBadge>

          <span
            data-testid="knowledge-hub-card-cue"
            aria-hidden="true"
            className="shrink-0 -translate-x-1 opacity-0 transition-[opacity,translate] duration-base ease-out group-hover:translate-x-0 group-hover:opacity-100 group-focus-within:translate-x-0 group-focus-within:opacity-100"
          >
            <Button asChild variant="ghost" size="sm">
              <span>
                {t('knowledgeHub.knowledgeHubCard.view')}
                <ArrowRightIcon
                  data-testid="knowledge-hub-card-arrow"
                  className="size-4 shrink-0"
                />
              </span>
            </Button>
          </span>
        </div>

        <div
          data-testid="knowledge-hub-card-text"
          className="flex w-full flex-1 flex-col items-start gap-md"
        >
          <div className="flex flex-col gap-sm">
            <h3 className="type-heading-md text-foreground">{title}</h3>
            <p className="type-body-md text-pretty text-foreground-alt">{description}</p>
          </div>

          {showCount && (
            <Badge
              data-testid="knowledge-hub-card-count"
              variant="outline"
              size="sm"
              roundness="full"
              className={clsx('mt-auto', count === 0 && 'text-muted-foreground')}
            >
              {countLabel ? `${count} ${countLabel}` : `${count}`}
            </Badge>
          )}
        </div>
      </Link>
    </Card>
  );
};

KnowledgeHubCard.displayName = 'KnowledgeHubCard';
